/**
 * ScopeTreeTable — reusable folder-tree + line-item list with inline editing
 * (description + note), click-to-expand notes, remove + undo (⌘/Ctrl+Z), an
 * expandable Details popover, and a collapsible area rail.
 *
 * Faithful TS+React+Tailwind port of design_reference/js/scope-tree-table.jsx
 * (the `.tt-*` family in design_reference/styles/app.css ported to Tailwind
 * arbitrary values + the V2 primitives). Used by Supplement and Scope Builder.
 *
 * The CAT/SEL code cells here are STATIC read-only (the flagship grid is the
 * editable treatment — see PRP Gotcha #6). Sorting is intentionally omitted;
 * the footer notes that sorting + grand-total recalculation happen in
 * Xactimate on export.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { fmtK, fmtMoney, unpricedNote } from '../lib/format';
import { ACT_LABEL } from '../lib/semantics';
import {
  IconChevr,
  IconChevd,
  IconDownload,
  IconInfo,
  IconPanel,
  IconPen,
  IconRefresh,
  IconTag,
  IconTrash,
} from './icons';
import { Button } from './ui/button';
import { Pill } from './ui/pill';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Kbd } from './ui/kbd';
import ChatTag from './ChatTag';
import { findNode, leavesUnder } from '../lib/scopeTreeNodes';
import { useTakePriceNotes } from '../lib/priceNoteLines';

/** A node in the area folder tree. */
export interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
  /**
   * Step 1 slice B: an authoritative RCV subtotal recovered from the PDF folder
   * block. When present it is shown verbatim (the source of truth before
   * export); when absent the node rolls up its leaf line-item totals. Container
   * (folder) nodes carry this; an unpriced parse would otherwise show $0.
   */
  subtotal?: number | null;
  /** PRP-MCP-5: a room a chat app added (the change is not undone). */
  fromChat?: boolean;
}

/** A single scope line item rendered in the list. */
export interface ScopeTreeItem {
  id: string;
  area: string;
  heading: string;
  n: number;
  cat: string;
  sel: string;
  act: string;
  qty: number | null;
  unit: string;
  total: number;
  /** PRP-MCP-7 G7: no price (chat changed or added it); shown as Unpriced, left out of totals. */
  unpriced?: boolean;
  /** PRP-IMPORT-PRICENOTE-1: shown in place of the price on an `unpriced` line. */
  priceNote?: string | null;
  desc: string;
  /** The user's own note on the line (starts empty on a fresh generation). */
  note?: string;
  /**
   * The engine's read-only justification for the line (why it's in the scope).
   * Shown on row click; "Add to line item note" copies it into `note`.
   */
  justification?: string;
  /** Xactimate sketch variable behind the qty (C/W/F/PF…), when derived. */
  calc?: string | null;
  /** Position of this line in the source scope (rooms[roomIdx].items[itemIdx])
   *  — lets the page map table mutations back onto the persisted scope. */
  roomIdx?: number;
  itemIdx?: number;
  /** PRP-MCP-5: a line a chat app changed (the change is not undone). */
  fromChat?: boolean;
}

/** A table mutation reported to the page (which owns the persisted scope). */
export type ScopeMutation =
  | { type: 'remove'; item: ScopeTreeItem }
  | { type: 'restore'; item: ScopeTreeItem }
  | {
      type: 'patch';
      item: ScopeTreeItem;
      patch: { desc?: string; note?: string; act?: string; sel?: string; unit?: string };
    };

/** A selector row offered by the SEL browser. */
export interface SelBrowseOption {
  sel: string;
  dsc: string;
  unit: string;
  actions: string[];
}

/** An action choice offered by the action editor. */
export interface ActionChoice {
  symbol: string;
  label: string;
}

/** The full data shape consumed by {@link ScopeTreeTable}. */
export interface ScopeTreeData {
  name: string;
  sub: string;
  priceList: string;
  ref: string;
  tree: TreeNode[];
  items: ScopeTreeItem[];
}

interface UndoEntry {
  item: ScopeTreeItem;
  idx: number;
}

interface AreaSum {
  t: number;
  c: number;
}

/** Props for {@link ScopeTreeTable}. */
export interface ScopeTreeTableProps {
  data: ScopeTreeData;
  /**
   * Admin/testing: when provided, the toolbar renders an "Export" button that
   * calls this handler (dump the form contents + generated scope for
   * troubleshooting). When omitted the button is hidden — non-admins download
   * the ESX from the page header instead.
   */
  onDebugExport?: () => void | Promise<void>;
  /**
   * When provided, the table is EDITABLE (remove always visible, action chip
   * opens an editor, SEL chip opens the catalog browser) and every local edit
   * is reported so the page can persist it into the saved scope.
   */
  onMutate?: (m: ScopeMutation) => void;
  /** Catalog selectors for a CAT (SEL browser). Required for SEL editing. */
  loadSelectors?: (cat: string) => Promise<SelBrowseOption[]>;
  /** Supported actions for a code (action editor). Optional; falls back to
   *  the common activity symbols. */
  loadActions?: (cat: string, sel: string) => Promise<ActionChoice[]>;
  /** An extra control for a line, shown with its row actions. */
  lineAction?: (it: ScopeTreeItem) => ReactNode;
}

const _FALLBACK_ACTIONS: ActionChoice[] = Object.entries(ACT_LABEL).map(
  ([symbol, label]) => ({ symbol, label: String(label) }),
);

/**
 * Reusable area-tree + line-item table.
 *
 * @param data - The estimate metadata, area tree, and line items to render.
 * @param onDebugExport - Optional admin handler; presence shows the Export button.
 * @param onMutate - Optional edit reporter; presence enables editing UI.
 * @param loadSelectors - Catalog selector loader (SEL browser).
 * @param loadActions - Supported-action loader (action editor).
 * @param lineAction - Optional extra row control.
 * @returns The tree-table element.
 */
export function ScopeTreeTable({
  data,
  onDebugExport,
  onMutate,
  loadSelectors,
  loadActions,
  lineAction,
}: ScopeTreeTableProps) {
  const [items, setItems] = useState<ScopeTreeItem[]>(data.items);
  useTakePriceNotes(data.items, setItems);
  const [sel, setSel] = useState<string>('__all');
  const [open, setOpen] = useState<Set<string>>(() => {
    // Open every container node (recursively) so a recovered folder chain
    // renders expanded by default — the founder sees the whole tree at a glance.
    const ids: string[] = ['main', 'upper', 'scope'];
    const walk = (nodes: TreeNode[]) =>
      nodes.forEach((n) => {
        ids.push(n.id);
        if (n.children) walk(n.children);
      });
    walk(data.tree);
    return new Set(ids);
  });
  // Multiple note panels can be open at once — deliberately, so a note can be
  // selected/copied from one line and pasted into another. Each panel has its
  // own ✕; opening one never closes the rest.
  const [openNotes, setOpenNotes] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<{ id: string; field: string } | null>(null);
  const [details, setDetails] = useState(false);
  const [railOpen, setRailOpen] = useState(() => window.innerWidth >= 768); // it fills a phone
  const [railWidth, setRailWidth] = useState(252);
  const [toast, setToast] = useState<string | null>(null);
  const undoStack = useRef<UndoEntry[]>([]);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ----- drag-to-resize the left area tree panel -----
  const RAIL_MIN = 180;
  const RAIL_MAX = 520;
  const startRailResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = railWidth;
      const onMove = (ev: MouseEvent) => {
        const next = Math.min(RAIL_MAX, Math.max(RAIL_MIN, startW + (ev.clientX - startX)));
        setRailWidth(next);
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [railWidth],
  );

  // ----- rollups -----
  const sums = useMemo(() => {
    const m: Record<string, AreaSum> = {};
    items.forEach((it) => {
      m[it.area] = m[it.area] || { t: 0, c: 0 };
      m[it.area].t += it.total;
      m[it.area].c++;
    });
    return m;
  }, [items]);

  const nodeSum = useCallback(
    (node: TreeNode): AreaSum =>
      leavesUnder(node).reduce<AreaSum>(
        (a, id) => {
          const s = sums[id] || { t: 0, c: 0 };
          return { t: a.t + s.t, c: a.c + s.c };
        },
        { t: 0, c: 0 },
      ),
    [sums],
  );

  const grandTotal = useMemo(() => items.reduce((s, i) => s + i.total, 0), [items]);
  const unpricedCount = useMemo(() => items.filter((i) => i.unpriced).length, [items]);

  // ----- selection -> visible leaf set -----
  const visibleAreas = useMemo(() => {
    if (sel === '__all') return null;
    const node = findNode(data.tree, sel);
    return node ? new Set(leavesUnder(node)) : new Set([sel]);
  }, [sel, data.tree]);

  // ----- group visible items by heading (first-seen order) -----
  const groups = useMemo(() => {
    const its = visibleAreas ? items.filter((i) => visibleAreas.has(i.area)) : items;
    const g: { name: string; rows: ScopeTreeItem[] }[] = [];
    const idx: Record<string, number> = {};
    its.forEach((it) => {
      const key = it.heading;
      if (!(key in idx)) {
        idx[key] = g.length;
        g.push({ name: key, rows: [] });
      }
      g[idx[key]].rows.push(it);
    });
    return g;
  }, [items, visibleAreas]);

  const shownTotal = useMemo(() => {
    const its = visibleAreas ? items.filter((i) => visibleAreas.has(i.area)) : items;
    return { t: its.reduce((s, i) => s + i.total, 0), c: its.length, u: its.filter((i) => i.unpriced).length };
  }, [items, visibleAreas]);

  // ----- remove + undo -----
  const removeItem = (it: ScopeTreeItem) => {
    const idx = items.findIndex((x) => x.id === it.id);
    undoStack.current.push({ item: it, idx });
    setItems(items.filter((x) => x.id !== it.id));
    setOpenNotes((prev) => {
      if (!prev.has(it.id)) return prev;
      const next = new Set(prev);
      next.delete(it.id);
      return next;
    });
    setToast(it.desc);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 6000);
    onMutate?.({ type: 'remove', item: it });
  };

  const undo = useCallback(() => {
    const last = undoStack.current.pop();
    if (!last) return;
    setItems((prev) => {
      const next = [...prev];
      next.splice(Math.min(last.idx, next.length), 0, last.item);
      return next;
    });
    setToast(null);
    onMutate?.({ type: 'restore', item: last.item });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMutate]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [undo]);

  // shared setter for desc + note
  const commit = (id: string, field: 'desc' | 'note', val: string) => {
    const target = items.find((x) => x.id === id);
    setItems(items.map((x) => (x.id === id ? { ...x, [field]: val } : x)));
    setEditing(null);
    if (target && target[field] !== val)
      onMutate?.({ type: 'patch', item: target, patch: { [field]: val } });
  };

  // ----- action editor + SEL browser (editable mode) -----
  const [actionEditor, setActionEditor] = useState<string | null>(null);
  const [actionChoices, setActionChoices] = useState<ActionChoice[] | null>(null);
  const [selBrowser, setSelBrowser] = useState<ScopeTreeItem | null>(null);
  const [selOptions, setSelOptions] = useState<SelBrowseOption[] | null>(null);
  const [selFilter, setSelFilter] = useState('');

  const openActionEditor = (it: ScopeTreeItem) => {
    setActionEditor(actionEditor === it.id ? null : it.id);
    setActionChoices(null);
    if (loadActions) {
      loadActions(it.cat, it.sel)
        .then((a) => setActionChoices(a.length ? a : _FALLBACK_ACTIONS))
        .catch(() => setActionChoices(_FALLBACK_ACTIONS));
    } else {
      setActionChoices(_FALLBACK_ACTIONS);
    }
  };

  const applyAction = (it: ScopeTreeItem, symbol: string) => {
    setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, act: symbol } : x)));
    setActionEditor(null);
    if (symbol !== it.act) onMutate?.({ type: 'patch', item: it, patch: { act: symbol } });
  };

  const openSelBrowser = (it: ScopeTreeItem) => {
    setSelBrowser(it);
    setSelOptions(null);
    setSelFilter('');
    loadSelectors?.(it.cat)
      .then((s) => setSelOptions(s))
      .catch(() => setSelOptions([]));
  };

  const applySel = (it: ScopeTreeItem, opt: SelBrowseOption) => {
    setItems((prev) =>
      prev.map((x) =>
        x.id === it.id ? { ...x, sel: opt.sel, desc: opt.dsc, unit: opt.unit } : x,
      ),
    );
    setSelBrowser(null);
    onMutate?.({
      type: 'patch',
      item: it,
      patch: { sel: opt.sel, desc: opt.dsc, unit: opt.unit },
    });
  };

  const openNotePanel = (id: string) =>
    setOpenNotes((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));

  const closeNotePanel = (id: string) =>
    setOpenNotes((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const toggleNotePanel = (id: string) =>
    setOpenNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** Copy the engine's justification into the user's editable note. */
  const adoptJustification = (it: ScopeTreeItem) => {
    setItems((prev) =>
      prev.map((x) => (x.id === it.id ? { ...x, note: x.justification || '' } : x)),
    );
    if (it.justification)
      onMutate?.({ type: 'patch', item: it, patch: { note: it.justification } });
  };

  const toggleNode = (id: string) =>
    setOpen((o) => {
      const x = new Set(o);
      if (x.has(id)) x.delete(id);
      else x.add(id);
      return x;
    });

  // ----- tree render (recursive) -----
  const renderTree = (nodes: TreeNode[], depth: number) =>
    nodes.map((n) => {
      const hasKids = !!n.children;
      const isOpen = open.has(n.id);
      const isSel = sel === n.id;
      const s = nodeSum(n);
      // Prefer the recovered RCV subtotal (authoritative, survives an unpriced
      // parse) over the leaf rollup, which is $0 until pricing lands (Step 4).
      const displayTotal =
        n.subtotal != null && n.subtotal !== 0 ? n.subtotal : s.t;
      return (
        <div key={n.id}>
          <div
            className={cn(
              'relative flex items-center gap-[7px] h-[30px] rounded-[var(--r-sm)] cursor-pointer pr-2',
              'text-[var(--tx-mid)] hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)]',
              isSel && 'bg-[var(--accent-soft)] text-[var(--tx-hi)]',
              isSel &&
                "before:content-[''] before:absolute before:left-0 before:top-[5px] before:bottom-[5px] before:w-[2.5px] before:rounded-full before:bg-[var(--accent)]",
            )}
            style={{ paddingLeft: 6 + depth * 15 }}
            onClick={() => {
              setSel(n.id);
              if (hasKids) toggleNode(n.id);
            }}
          >
            <span
              className="w-4 h-4 flex-none grid place-items-center text-[var(--tx-faint)] rounded-[4px]"
              onClick={(e) => {
                if (hasKids) {
                  e.stopPropagation();
                  toggleNode(n.id);
                }
              }}
            >
              {hasKids && (
                <IconChevr
                  className={cn(
                    'w-3 h-3 transition-transform duration-[120ms]',
                    isOpen && 'rotate-90',
                  )}
                />
              )}
            </span>
            <span
              className={cn(
                // B-183: nowrap WITHOUT ellipsis so the row grows to its
                // natural width and the rail scrolls horizontally rather than
                // truncating the label on deep folder nesting.
                'flex-1 whitespace-nowrap text-[12.5px] pr-2',
                hasKids && 'font-[550]',
              )}
            >
              {n.label}
              {n.fromChat && <ChatTag className="ml-1.5" />}
            </span>
            <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--tx-faint)] min-w-4 text-right">
              {s.c}
            </span>
            <span
              className={cn(
                'font-[var(--font-mono)] text-[10.5px]',
                isSel ? 'text-[var(--accent-ink)]' : 'text-[var(--tx-lo)]',
              )}
            >
              {fmtK(displayTotal)}
            </span>
          </div>
          {hasKids && isOpen && (
            <div
              className="relative before:content-[''] before:absolute before:top-0 before:bottom-[6px] before:left-0 before:w-px before:bg-[var(--bd-subtle)]"
              style={{ marginLeft: 6 + depth * 15 }}
            >
              {renderTree(n.children!, depth + 1)}
            </div>
          )}
        </div>
      );
    });

  return (
    <div
      className="h-full flex flex-col bg-[var(--bg-0)]"
      onClick={() => {
        setDetails(false);
        setActionEditor(null);
      }}
    >
      {/* top bar with expandable Details popover */}
      <div
        className="relative flex-none min-h-[48px] flex items-center gap-3 px-4 py-2 border-b border-[var(--bd-subtle)] bg-[var(--bg-1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="ghost"
          size="sm"
          className="w-7 px-0 justify-center"
          title={railOpen ? 'Hide areas panel' : 'Show areas panel'}
          onClick={() => setRailOpen((o) => !o)}
        >
          <IconPanel />
        </Button>
        <span className="font-[var(--font-mono)] text-[13px] font-semibold text-[var(--tx-hi)]">
          {data.name}
        </span>
        <Pill kind="neutral">
          <IconTag className="!w-[11px] !h-[11px]" />
          {data.ref}
        </Pill>
        <div className="flex-1" />
        <Button size="sm" onClick={() => setDetails((d) => !d)}>
          <IconInfo />
          Details
          <IconChevd className="!w-[13px] !h-[13px]" />
        </Button>
        {onDebugExport && (
          <Button
            size="sm"
            onClick={onDebugExport}
            title="Copy the form contents + generated scope as JSON for troubleshooting"
            data-testid="scope-tree-export"
          >
            <IconDownload />
            Export
          </Button>
        )}
        {details && (
          <div
            className="absolute top-[46px] left-4 z-50 w-[320px] p-[14px] rounded-[var(--r-lg)] bg-[var(--bg-2)] border border-[var(--bd-strong)] shadow-[var(--sh-pop)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] font-semibold tracking-[.06em] uppercase text-[var(--tx-faint)] mb-2">
              Estimate details
            </div>
            <DetailRow k="RCV">
              <span className="font-[var(--font-mono)]">{fmtMoney(grandTotal, true)}{unpricedNote(unpricedCount)}</span>
            </DetailRow>
            <DetailRow k="Line items">
              <span className="font-[var(--font-mono)]">{items.length}</span>
            </DetailRow>
            <DetailRow k="Price list">
              <span className="font-[var(--font-mono)]">{data.priceList}</span>
            </DetailRow>
            <DetailRow k="Source" last>
              {data.sub}
            </DetailRow>
          </div>
        )}
      </div>

      <div className="flex-1 flex min-h-0">
        {/* custom modern folder tree */}
        {railOpen && (
          <div
            className="relative flex-none border-r border-[var(--bd-subtle)] bg-[var(--bg-1)] overflow-auto px-2 pt-2 pb-4"
            style={{ width: railWidth }}
          >
            {/* drag handle on the right edge to widen/narrow the panel */}
            <div
              role="separator"
              aria-orientation="vertical"
              title="Drag to resize"
              className="absolute top-0 right-0 z-10 h-full w-[6px] cursor-col-resize hover:bg-[var(--accent-soft)]"
              onMouseDown={startRailResize}
              onClick={(e) => e.stopPropagation()}
            />
            {/* B-183: min-w-max lets deeply-nested rows take their natural
                width so the rail's overflow-auto provides a HORIZONTAL
                scrollbar instead of truncating folder/line-item labels and
                pushing the list off-screen. */}
            <div className="min-w-max">
              <div
                className={cn(
                  'relative flex items-center gap-[7px] h-[30px] rounded-[var(--r-sm)] cursor-pointer pr-2 font-semibold',
                  'text-[var(--tx-mid)] hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)]',
                  sel === '__all' && 'bg-[var(--accent-soft)] text-[var(--tx-hi)]',
                  sel === '__all' &&
                    "before:content-[''] before:absolute before:left-0 before:top-[5px] before:bottom-[5px] before:w-[2.5px] before:rounded-full before:bg-[var(--accent)]",
                )}
                style={{ paddingLeft: 8 }}
                onClick={() => setSel('__all')}
              >
                <span className="w-4 h-4 flex-none" />
                <span className="flex-1 whitespace-nowrap text-[12.5px] pr-2">
                  All Items
                </span>
                <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--tx-faint)] min-w-4 text-right">
                  {items.length}
                </span>
                <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--accent-ink)]" title={unpricedNote(unpricedCount).trim() || undefined}>
                  {fmtK(grandTotal)}
                </span>
              </div>
              <div className="h-2" />
              {renderTree(data.tree, 0)}
            </div>
          </div>
        )}

        {/* line-item list with inline headings */}
        <div className="flex-1 min-w-0 overflow-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr>
                <th className="sticky top-0 z-[2] bg-[var(--bg-1)] text-right text-[10.5px] font-semibold text-[var(--tx-lo)] tracking-[.03em] uppercase h-[34px] px-3 border-b border-[var(--bd)] whitespace-nowrap w-[44px]">
                  #
                </th>
                <th className="sticky top-0 z-[2] bg-[var(--bg-1)] text-left text-[10.5px] font-semibold text-[var(--tx-lo)] tracking-[.03em] uppercase h-[34px] px-3 border-b border-[var(--bd)] whitespace-nowrap w-[56px]">
                  CAT
                </th>
                <th className="sticky top-0 z-[2] bg-[var(--bg-1)] text-left text-[10.5px] font-semibold text-[var(--tx-lo)] tracking-[.03em] uppercase h-[34px] px-3 border-b border-[var(--bd)] whitespace-nowrap w-[66px]">
                  SEL
                </th>
                <th className="sticky top-0 z-[2] bg-[var(--bg-1)] text-left text-[10.5px] font-semibold text-[var(--tx-lo)] tracking-[.03em] uppercase h-[34px] px-3 border-b border-[var(--bd)] whitespace-nowrap w-[64px]">
                  Action
                </th>
                <th className="sticky top-0 z-[2] bg-[var(--bg-1)] text-left text-[10.5px] font-semibold text-[var(--tx-lo)] tracking-[.03em] uppercase h-[34px] px-3 border-b border-[var(--bd)] whitespace-nowrap">
                  Description
                </th>
                <th className="sticky top-0 z-[2] bg-[var(--bg-1)] text-left text-[10.5px] font-semibold text-[var(--tx-lo)] tracking-[.03em] uppercase h-[34px] px-3 border-b border-[var(--bd)] whitespace-nowrap w-[56px]">
                  Calc
                </th>
                <th className="sticky top-0 z-[2] bg-[var(--bg-1)] text-right text-[10.5px] font-semibold text-[var(--tx-lo)] tracking-[.03em] uppercase h-[34px] px-3 border-b border-[var(--bd)] whitespace-nowrap w-[100px]">
                  Qty
                </th>
                <th className="sticky top-0 z-[2] bg-[var(--bg-1)] text-right text-[10.5px] font-semibold text-[var(--tx-lo)] tracking-[.03em] uppercase h-[34px] px-3 border-b border-[var(--bd)] whitespace-nowrap w-[108px]">
                  Total
                </th>
                <th className="sticky top-0 z-[2] bg-[var(--bg-1)] h-[34px] px-3 border-b border-[var(--bd)] w-[70px]" />
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const gsum = g.rows.reduce((s, r) => s + r.total, 0);
                return (
                  <Fragment key={g.name}>
                    <tr>
                      <td colSpan={9} className="p-0">
                        <div className="flex items-center gap-[10px] pt-3 px-[14px] pb-[6px]">
                          <span className="text-[11px] font-bold tracking-[.05em] uppercase text-[var(--tx-mid)] whitespace-nowrap">
                            {g.name}
                          </span>
                          <span className="flex-1 h-px bg-[var(--bd-subtle)]" />
                          <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--tx-faint)]">
                            {g.rows.length} · {fmtMoney(gsum, true)}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {g.rows.map((it) => {
                      const isNote = openNotes.has(it.id);
                      const isEd = editing != null && editing.id === it.id;
                      return (
                        <Fragment key={it.id}>
                          <tr
                            className="group border-b border-[var(--bd-subtle)] hover:bg-[var(--bg-2)] cursor-pointer"
                            title={isNote ? undefined : 'Click to see why this line item is in the scope'}
                            onClick={() => toggleNotePanel(it.id)}
                          >
                            <td className="h-[var(--row-h)] px-3 align-middle text-right font-[var(--font-mono)] text-[var(--tx-faint)]">
                              {it.n}
                            </td>
                            <td className="h-[var(--row-h)] px-3 align-middle text-[var(--tx-mid)]">
                              <span className="inline-flex items-center gap-1 h-[22px] px-[6px] rounded-[var(--r-xs)] border border-[var(--bd)] bg-[var(--bg-2)] font-[var(--font-mono)] text-[11px] text-[var(--accent-ink)] tracking-[.02em]">
                                {it.cat}
                              </span>
                            </td>
                            <td className="h-[var(--row-h)] px-3 align-middle text-[var(--tx-mid)]">
                              <span
                                className={cn(
                                  'inline-flex items-center gap-1 h-[22px] px-[6px] rounded-[var(--r-xs)] border border-[var(--bd)] bg-[var(--bg-2)] font-[var(--font-mono)] text-[11px] text-[var(--tx-mid)] tracking-[.02em]',
                                  onMutate &&
                                    loadSelectors &&
                                    'cursor-pointer hover:border-[var(--accent)] hover:text-[var(--tx-hi)]',
                                )}
                                title={
                                  onMutate && loadSelectors
                                    ? 'Click to browse the catalog for this category'
                                    : undefined
                                }
                                onClick={
                                  onMutate && loadSelectors
                                    ? (e) => {
                                        e.stopPropagation();
                                        openSelBrowser(it);
                                      }
                                    : undefined
                                }
                              >
                                {it.sel}
                              </span>
                            </td>
                            <td className="relative h-[var(--row-h)] px-3 align-middle text-[var(--tx-mid)]">
                              <span
                                className={cn(
                                  'inline-flex items-center justify-center h-[22px] min-w-[22px] px-[6px] rounded-[var(--r-xs)] border border-[var(--bd)] bg-[var(--bg-2)] font-[var(--font-mono)] text-[11px] text-[var(--tx-mid)] tracking-[.02em]',
                                  onMutate &&
                                    'cursor-pointer hover:border-[var(--accent)] hover:text-[var(--tx-hi)]',
                                )}
                                title={
                                  onMutate
                                    ? `${ACT_LABEL[it.act] ?? it.act ?? ''} — click to change`
                                    : (ACT_LABEL[it.act] ?? it.act)
                                }
                                onClick={
                                  onMutate
                                    ? (e) => {
                                        e.stopPropagation();
                                        openActionEditor(it);
                                      }
                                    : undefined
                                }
                              >
                                {it.act || '—'}
                              </span>
                              {actionEditor === it.id && (
                                <div
                                  className="absolute top-[calc(100%-4px)] left-2 z-50 min-w-[220px] py-1 rounded-[var(--r-lg)] bg-[var(--bg-2)] border border-[var(--bd-strong)] shadow-[var(--sh-pop)]"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {(actionChoices ?? []).map((a) => (
                                    <button
                                      key={a.symbol}
                                      type="button"
                                      className={cn(
                                        'flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] hover:bg-[var(--bg-3)]',
                                        a.symbol === it.act && 'text-[var(--accent-ink)]',
                                      )}
                                      onClick={() => applyAction(it, a.symbol)}
                                    >
                                      <span className="inline-flex items-center justify-center h-[20px] min-w-[20px] px-1 rounded-[var(--r-xs)] border border-[var(--bd)] font-[var(--font-mono)] text-[11px]">
                                        {a.symbol}
                                      </span>
                                      {a.label}
                                    </button>
                                  ))}
                                  {actionChoices === null && (
                                    <div className="px-3 py-2 text-[11.5px] text-[var(--tx-lo)]">
                                      Loading actions…
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="h-[var(--row-h)] px-3 align-middle text-[var(--tx-mid)]">
                              {isEd ? (
                                <Input
                                  autoFocus
                                  defaultValue={it.desc}
                                  className="h-auto py-[4px] px-[7px] text-[12.5px] rounded-[5px] border-[var(--accent)] bg-[var(--bg-inset)] shadow-[0_0_0_3px_var(--accent-soft)]"
                                  onBlur={(e) => commit(it.id, 'desc', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter')
                                      commit(it.id, 'desc', (e.target as HTMLInputElement).value);
                                    if (e.key === 'Escape') setEditing(null);
                                  }}
                                />
                              ) : (
                                <span
                                  className="text-[var(--tx-hi)] font-[450] cursor-text rounded-[4px] py-[3px] px-[6px] -my-[3px] -mx-[6px] hover:bg-[var(--bg-3)] hover:shadow-[inset_0_0_0_1px_var(--bd)]"
                                  title="Click to edit"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditing({ id: it.id, field: 'desc' });
                                  }}
                                >
                                  {it.desc}
                                  {it.fromChat && <ChatTag className="ml-1.5" />}
                                </span>
                              )}
                            </td>
                            <td className="h-[var(--row-h)] px-3 align-middle text-[var(--tx-mid)]">
                              {it.calc ? (
                                <span
                                  className="inline-flex items-center justify-center h-[22px] min-w-[22px] px-[6px] rounded-[var(--r-xs)] border border-[var(--bd)] bg-[var(--bg-2)] font-[var(--font-mono)] text-[11px] text-[var(--tx-mid)] tracking-[.02em]"
                                  title="Xactimate sketch variable — the quantity recalculates from the sketch"
                                >
                                  {it.calc}
                                </span>
                              ) : (
                                <span className="text-[var(--tx-faint)]">—</span>
                              )}
                            </td>
                            <td className="h-[var(--row-h)] px-3 align-middle text-right font-[var(--font-mono)] text-[var(--tx-mid)]">
                              {it.qty != null ? it.qty.toLocaleString() : '—'}{' '}
                              <span className="text-[var(--tx-faint)] text-[11px]">{it.unit}</span>
                            </td>
                            <td
                              className="h-[var(--row-h)] px-3 align-middle text-right font-[var(--font-mono)] font-[530] text-[var(--tx-hi)]"
                            >
                              {it.unpriced ? it.priceNote || 'Unpriced' : fmtMoney(it.total, true)}
                            </td>
                            <td className="h-[var(--row-h)] px-3 align-middle text-[var(--tx-mid)]">
                              <div className="flex items-center justify-end gap-[2px]">
                                {lineAction?.(it)}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={cn(
                                    'w-7 px-0 justify-center hover:text-[var(--bad)] hover:border-[color-mix(in_oklab,var(--bad)_40%,transparent)]',
                                    // Editable mode keeps the remove control visible
                                    // (hover-reveal read as "no way to remove a line").
                                    !onMutate && 'opacity-0 group-hover:opacity-100',
                                  )}
                                  title="Remove line item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeItem(it);
                                  }}
                                >
                                  <IconTrash className="!w-[14px] !h-[14px]" />
                                </Button>
                                <button
                                  type="button"
                                  className={cn(
                                    'relative w-[26px] h-[26px] grid place-items-center rounded-[var(--r-sm)] border border-transparent bg-transparent',
                                    'text-[var(--tx-faint)] hover:text-[var(--tx-mid)] hover:bg-[var(--bg-3)]',
                                    it.note &&
                                      "text-[var(--accent-ink)] after:content-[''] after:absolute after:top-1 after:right-1 after:w-[5px] after:h-[5px] after:rounded-full after:bg-[var(--accent-ink)]",
                                    isNote && 'bg-[var(--accent-soft)] text-[var(--accent-ink)]',
                                  )}
                                  title={it.note ? 'View / edit note' : 'Add a note'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleNotePanel(it.id);
                                  }}
                                >
                                  <IconPen className="w-[14px] h-[14px]" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {isNote && (
                            <tr>
                              <td colSpan={9} className="p-0">
                                <div className="relative bg-[var(--bg-1)] border-l-[2.5px] border-[var(--accent)] px-4 pt-[11px] pb-[13px]">
                                  {/* ✕ closes THIS panel only — others stay open so
                                      notes can be copied between line items. */}
                                  <button
                                    type="button"
                                    aria-label="Close note"
                                    title="Close"
                                    className="absolute top-[8px] right-[10px] w-[22px] h-[22px] grid place-items-center rounded-[var(--r-sm)] text-[var(--tx-faint)] hover:text-[var(--tx-hi)] hover:bg-[var(--bg-3)]"
                                    onClick={() => closeNotePanel(it.id)}
                                  >
                                    ✕
                                  </button>
                                  {it.note ? (
                                    <>
                                      <div className="flex items-center gap-[6px] text-[10px] font-semibold tracking-[.06em] uppercase text-[var(--tx-faint)] mb-[6px]">
                                        <IconPen className="w-3 h-3" />
                                        Line item note
                                      </div>
                                      <Textarea
                                        defaultValue={it.note}
                                        placeholder="Add a note for this line item…"
                                        className="max-w-[640px] min-h-[54px]"
                                        onBlur={(e) => commit(it.id, 'note', e.target.value)}
                                      />
                                    </>
                                  ) : it.justification ? (
                                    <>
                                      <div className="flex items-center gap-[6px] text-[10px] font-semibold tracking-[.06em] uppercase text-[var(--tx-faint)] mb-[6px]">
                                        <IconInfo className="w-3 h-3" />
                                        Why this line item
                                      </div>
                                      <div className="max-w-[640px] text-[12.5px] text-[var(--tx-mid)] whitespace-pre-wrap">
                                        {it.justification}
                                      </div>
                                      <Button
                                        size="sm"
                                        className="mt-[10px]"
                                        title="Copy this justification into the line item's editable note"
                                        onClick={() => adoptJustification(it)}
                                      >
                                        <IconPen className="!w-[13px] !h-[13px]" />
                                        Add to line item note
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <div className="flex items-center gap-[6px] text-[10px] font-semibold tracking-[.06em] uppercase text-[var(--tx-faint)] mb-[6px]">
                                        <IconPen className="w-3 h-3" />
                                        Line item note
                                      </div>
                                      <Textarea
                                        defaultValue=""
                                        placeholder="Add a note for this line item…"
                                        className="max-w-[640px] min-h-[54px]"
                                        onBlur={(e) => commit(it.id, 'note', e.target.value)}
                                      />
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-[14px] text-[var(--tx-faint)] text-[11.5px]">
            Showing {shownTotal.c} line items · {fmtMoney(shownTotal.t, true)}{unpricedNote(shownTotal.u)} · Sorting &amp;
            grand-total recalculation happen in Xactimate on export.
          </div>
        </div>
      </div>

      {selBrowser && (
        <div
          className="fixed inset-0 z-[95] grid place-items-center bg-black/40"
          onClick={() => setSelBrowser(null)}
        >
          <div
            className="w-[560px] max-h-[70vh] flex flex-col rounded-[var(--r-lg)] bg-[var(--bg-1)] border border-[var(--bd-strong)] shadow-[var(--sh-pop)]"
            onClick={(e) => e.stopPropagation()}
            data-testid="sel-browser"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--bd-subtle)]">
              <span className="text-[12.5px] font-semibold text-[var(--tx-hi)]">
                Browse {selBrowser.cat} selectors
              </span>
              <span className="text-[11.5px] text-[var(--tx-lo)]">
                replacing {selBrowser.cat} {selBrowser.sel}
              </span>
              <div className="flex-1" />
              <button
                type="button"
                aria-label="Close selector browser"
                className="w-[22px] h-[22px] grid place-items-center rounded-[var(--r-sm)] text-[var(--tx-faint)] hover:text-[var(--tx-hi)] hover:bg-[var(--bg-3)]"
                onClick={() => setSelBrowser(null)}
              >
                ✕
              </button>
            </div>
            <div className="px-4 py-2 border-b border-[var(--bd-subtle)]">
              <Input
                autoFocus
                placeholder="Filter selectors…"
                value={selFilter}
                onChange={(e) => setSelFilter(e.target.value)}
              />
            </div>
            <div className="flex-1 overflow-auto py-1">
              {selOptions === null && (
                <div className="px-4 py-3 text-[11.5px] text-[var(--tx-lo)]">
                  Loading selectors…
                </div>
              )}
              {selOptions !== null && selOptions.length === 0 && (
                <div className="px-4 py-3 text-[11.5px] text-[var(--tx-lo)]">No selectors.</div>
              )}
              {(selOptions ?? [])
                .filter((o) => {
                  const f = selFilter.trim().toLowerCase();
                  return (
                    !f ||
                    o.sel.toLowerCase().includes(f) ||
                    (o.dsc || '').toLowerCase().includes(f)
                  );
                })
                .slice(0, 200)
                .map((o) => (
                  <button
                    key={o.sel}
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-[7px] text-left text-[12.5px] hover:bg-[var(--bg-3)]',
                      o.sel === selBrowser.sel && 'bg-[var(--accent-soft)]',
                    )}
                    onClick={() => applySel(selBrowser, o)}
                  >
                    <span className="inline-flex items-center h-[22px] px-[6px] rounded-[var(--r-xs)] border border-[var(--bd)] bg-[var(--bg-2)] font-[var(--font-mono)] text-[11px] text-[var(--tx-mid)] min-w-[64px] justify-center">
                      {o.sel}
                    </span>
                    <span className="flex-1 text-[var(--tx-hi)]">{o.dsc}</span>
                    <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--tx-faint)]">
                      {o.unit}
                    </span>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          className="fixed bottom-[22px] left-1/2 -translate-x-1/2 z-[90] flex items-center gap-[14px] py-[9px] pr-[11px] pl-[15px] rounded-[var(--r-lg)] bg-[var(--bg-3)] border border-[var(--bd-strong)] shadow-[var(--sh-lg)] text-[12.5px] text-[var(--tx-hi)]"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-[var(--tx-mid)] max-w-[320px] whitespace-nowrap overflow-hidden text-ellipsis">
            Removed “{toast}”
          </span>
          <Button size="sm" onClick={undo}>
            <IconRefresh className="!w-[13px] !h-[13px]" />
            Undo
          </Button>
          <Kbd>⌘Z</Kbd>
        </div>
      )}
    </div>
  );
}

/** A single row in the Details popover. */
function DetailRow({
  k,
  children,
  last,
}: {
  k: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex justify-between items-center py-[7px] text-[12.5px]',
        !last && 'border-b border-[var(--bd-subtle)]',
      )}
    >
      <span className="text-[var(--tx-lo)]">{k}</span>
      <span className="text-[var(--tx-hi)] font-[550]">{children}</span>
    </div>
  );
}
